import { Component, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, GATE_EVENT, isGateError } from "../lib/api";
import { useLocation } from "react-router-dom";
import GatePage from "../components/site/GatePage";
import { GateLegalView } from "../components/site/GateLegal";
import { LEGAL_SLUGS } from "../pages/public/LegalPage";

const SiteContext = createContext(null);
const LOGO = `${process.env.PUBLIC_URL || ""}/brand/logo-480.webp`;
const TIMEOUT_MS = 7000;

// Branded shell shown while loading or when the API is unreachable: never a blank screen.
export const SiteShell = ({ state, onRetry, retrying }) => (
  <div className="flex min-h-screen flex-col bg-[#F4F8FB]" data-testid={state === "error" ? "site-load-error" : "site-loading"} aria-busy={state !== "error"}>
    <header className="bg-black px-6 py-4"><img src={LOGO} alt="CIArb Bahamas Branch" className="h-10 w-auto" /></header>
    <main className="flex flex-1 items-center justify-center px-6">
      <div className="max-w-lg rounded-lg bg-white p-8 text-center shadow-sm">
        <h1 className="font-serif text-2xl text-[#0B2545]">Arbitration Day 2026</h1>
        {state === "error" ? (
          <>
            <p className="mt-4 text-[#1F2937]" role="alert" data-testid="site-unavailable-message">
              The site is temporarily unavailable. {retrying ? "Connecting…" : "We keep reconnecting automatically."}
            </p>
            <button type="button" onClick={onRetry} disabled={retrying} className="mt-6 rounded bg-[#C8102E] px-5 py-2 font-semibold text-white disabled:opacity-60" data-testid="site-retry-btn">
              {retrying ? "Connecting…" : "Try again"}
            </button>
          </>
        ) : <p className="mt-4 text-[#1F2937]" role="status">Connecting…</p>}
      </div>
    </main>
  </div>
);

// Last-resort guard: a render crash anywhere shows the branded shell, not a blank page.
export class AppErrorBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <SiteShell state="error" onRetry={() => window.location.reload()} /> : this.props.children;
  }
}

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const [state, setState] = useState("loading");
  const [retrying, setRetrying] = useState(false);
  const timer = useRef(null);
  const attempt = useRef(0);
  const inflight = useRef(false);
  const { pathname } = useLocation();

  // Any failure (network, abort, CORS, timeout, 4xx/5xx) shows the outage card at once; retries continue with backoff.
  // Exception: 401 gate_required means the API is up and the site gate is on, so show the gate page instead.
  const load = useCallback(() => {
    if (inflight.current) return;
    clearTimeout(timer.current);
    inflight.current = true;
    api.get("/public/bootstrap", { timeout: TIMEOUT_MS, params: { _fresh: Date.now() } })
      .then((r) => { attempt.current = 0; setSite(r.data); })
      .catch((e) => {
        if (isGateError(e)) { setState("gate"); return; }
        setState("error");
        attempt.current += 1;
        timer.current = setTimeout(load, Math.min(30000, 2000 * 2 ** (attempt.current - 1)));
      })
      .finally(() => { inflight.current = false; setRetrying(false); });
  }, []);

  useEffect(() => {
    load();
    const onGate = () => { clearTimeout(timer.current); setSite(null); setState("gate"); };
    window.addEventListener(GATE_EVENT, onGate);
    return () => { clearTimeout(timer.current); window.removeEventListener(GATE_EVENT, onGate); };
  }, [load]);
  // Navigation back from Admin and returning to a tab must refresh shared mode metadata.
  useEffect(() => { load(); }, [pathname, load]);
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [load]);
  useEffect(() => {
    if (site?.settings?.seo?.title) document.title = site.settings.seo.title;
  }, [site]);

  const legalSlug = LEGAL_SLUGS.find((s) => pathname.replace(/\/+$/, "") === `/${s}`);
  if (!site && state === "gate") return legalSlug ? <GateLegalView slug={legalSlug} /> : <GatePage onUnlocked={() => { setState("loading"); load(); }} />;
  if (!site) return <SiteShell state={state} retrying={retrying} onRetry={() => { setRetrying(true); load(); }} />;
  return <SiteContext.Provider value={{ ...site, reload: load }}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
